import { StorageService, STORAGE_KEYS } from "../services/storage";
import { getSystemSettings, SystemSettings } from "./commissionConfig";
import { CourtItem } from "../types/userProfile";
import {
  CommercialServiceType,
  CommercialScopeType,
  CommercialExemptionType,
  CommercialCourtesy,
  CommercialAuditLog,
  ResolvedCommercialRule,
  CommercialOperationSnapshot,
} from "../types/commercialRules";
import {
  saveCommercialCourtesiesFirestore,
  saveCommercialAuditLogsFirestore,
} from "./firebase";

export const COMMERCIAL_RULE_VERSION = "2.0.0";

/**
 * Carrega a lista de cortesias comerciais ativas e históricas.
 */
export function loadCommercialCourtesies(): CommercialCourtesy[] {
  try {
    const list = StorageService.get<CommercialCourtesy[]>(STORAGE_KEYS.COMMERCIAL_COURTESIES, []);
    if (Array.isArray(list)) {
      return list;
    }
  } catch (err) {
    console.error("Erro ao carregar cortesias comerciais:", err);
  }
  return [];
}

/**
 * Salva ou atualiza uma cortesia comercial no Storage e emite evento de sincronização.
 */
export function saveCommercialCourtesy(
  courtesy: CommercialCourtesy,
  userEmail: string = "superadmin@hubesportivo.com"
): CommercialCourtesy[] {
  const current = loadCommercialCourtesies();
  const index = current.findIndex((c) => c.id === courtesy.id);
  let updatedList: CommercialCourtesy[];

  const previousValue = index >= 0 ? current[index] : null;

  if (index >= 0) {
    updatedList = [...current];
    updatedList[index] = {
      ...courtesy,
      updatedAt: new Date().toISOString(),
    };
  } else {
    updatedList = [
      {
        ...courtesy,
        createdAt: courtesy.createdAt || new Date().toISOString(),
      },
      ...current,
    ];
  }

  StorageService.set(STORAGE_KEYS.COMMERCIAL_COURTESIES, updatedList);

  // Sincroniza com o Firestore
  saveCommercialCourtesiesFirestore(updatedList).catch((err) => {
    console.error("Erro ao sincronizar cortesias no Firestore:", err);
  });

  // Registrar log de auditoria
  addCommercialAuditLog({
    userId: "admin_super",
    userEmail,
    action: previousValue ? "UPDATE_COURTESY" : "CREATE_COURTESY",
    targetScope: "COURTESY",
    targetId: courtesy.id,
    targetName: courtesy.title,
    previousValue,
    newValue: courtesy,
    reason: courtesy.reason || "Criação/Edição de cortesia comercial",
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("commercial_courtesies_updated", { detail: updatedList }));
  }

  return updatedList;
}

/**
 * Alterna status (ativa/inativa) de uma cortesia comercial.
 */
export function toggleCommercialCourtesy(
  courtesyId: string,
  userEmail: string = "superadmin@hubesportivo.com"
): CommercialCourtesy[] {
  const current = loadCommercialCourtesies();
  const target = current.find((c) => c.id === courtesyId);
  if (!target) return current;

  const nextState = !target.active;
  const updated = current.map((c) =>
    c.id === courtesyId ? { ...c, active: nextState, updatedAt: new Date().toISOString() } : c
  );

  StorageService.set(STORAGE_KEYS.COMMERCIAL_COURTESIES, updated);

  saveCommercialCourtesiesFirestore(updated).catch((err) => {
    console.error("Erro ao sincronizar cortesias no Firestore:", err);
  });

  addCommercialAuditLog({
    userId: "admin_super",
    userEmail,
    action: nextState ? "ACTIVATE_COURTESY" : "DEACTIVATE_COURTESY",
    targetScope: "COURTESY",
    targetId: courtesyId,
    targetName: target.title,
    previousValue: { active: !nextState },
    newValue: { active: nextState },
    reason: `Cortesia ${nextState ? "ativada" : "desativada"} pelo Super Admin`,
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("commercial_courtesies_updated", { detail: updated }));
  }

  return updated;
}

/**
 * Remove uma cortesia comercial.
 */
export function deleteCommercialCourtesy(
  courtesyId: string,
  userEmail: string = "superadmin@hubesportivo.com"
): CommercialCourtesy[] {
  const current = loadCommercialCourtesies();
  const target = current.find((c) => c.id === courtesyId);
  const updated = current.filter((c) => c.id !== courtesyId);

  StorageService.set(STORAGE_KEYS.COMMERCIAL_COURTESIES, updated);

  saveCommercialCourtesiesFirestore(updated).catch((err) => {
    console.error("Erro ao sincronizar cortesias no Firestore:", err);
  });

  if (target) {
    addCommercialAuditLog({
      userId: "admin_super",
      userEmail,
      action: "DELETE_COURTESY",
      targetScope: "COURTESY",
      targetId: courtesyId,
      targetName: target.title,
      previousValue: target,
      newValue: null,
      reason: "Exclusão de cortesia comercial",
    });
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("commercial_courtesies_updated", { detail: updated }));
  }

  return updated;
}

/**
 * Carrega logs de auditoria administrativa de regras comerciais.
 */
export function loadCommercialAuditLogs(): CommercialAuditLog[] {
  try {
    const logs = StorageService.get<CommercialAuditLog[]>(STORAGE_KEYS.COMMERCIAL_AUDIT_LOGS, []);
    if (Array.isArray(logs)) {
      return logs;
    }
  } catch (err) {
    console.error("Erro ao carregar logs de auditoria comercial:", err);
  }
  return [];
}

/**
 * Adiciona um registro de auditoria comercial.
 */
export function addCommercialAuditLog(logData: Omit<CommercialAuditLog, "id" | "timestamp">): CommercialAuditLog {
  const logs = loadCommercialAuditLogs();
  const newLog: CommercialAuditLog = {
    id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    timestamp: new Date().toISOString(),
    ...logData,
  };

  const updatedLogs = [newLog, ...logs].slice(0, 100); // Mantém os últimos 100 logs
  StorageService.set(STORAGE_KEYS.COMMERCIAL_AUDIT_LOGS, updatedLogs);

  saveCommercialAuditLogsFirestore(updatedLogs).catch((err) => {
    console.error("Erro ao sincronizar logs de auditoria no Firestore:", err);
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("commercial_audit_logs_updated", { detail: updatedLogs }));
  }

  return newLog;
}

/**
 * Avalia se uma cortesia comercial é válida para a data e escopo fornecidos.
 */
export function isCourtesyValidForContext(
  courtesy: CommercialCourtesy,
  service: CommercialServiceType,
  targetDate: string,
  courtId?: string,
  courtName?: string,
  arenaId?: string,
  arenaName?: string
): boolean {
  if (!courtesy.active) return false;

  // Verifica compatibilidade de serviço
  if (courtesy.service !== "ALL" && courtesy.service !== service) {
    return false;
  }

  // Normalização de datas (YYYY-MM-DD)
  const normTarget = targetDate ? targetDate.substring(0, 10) : new Date().toISOString().substring(0, 10);
  const normStart = courtesy.startDate ? courtesy.startDate.substring(0, 10) : "1970-01-01";

  if (normTarget < normStart) {
    return false;
  }

  if (courtesy.endDate) {
    const normEnd = courtesy.endDate.substring(0, 10);
    if (normTarget > normEnd) {
      return false;
    }
  }

  // Verifica escopo
  if (courtesy.scope === "PLATFORM") {
    return true;
  }

  if (courtesy.scope === "ARENA") {
    const targetAId = arenaId || "";
    const targetAName = (arenaName || "").trim().toLowerCase();
    const scopeId = courtesy.scopeId || "";
    const scopeName = (courtesy.scopeName || "").trim().toLowerCase();

    if (scopeId && targetAId && scopeId === targetAId) return true;
    if (scopeName && targetAName && (scopeName === targetAName || targetAName.includes(scopeName))) return true;
    return false;
  }

  if (courtesy.scope === "COURT") {
    const targetCId = courtId || "";
    const targetCName = (courtName || "").trim().toLowerCase();
    const scopeId = courtesy.scopeId || "";
    const scopeName = (courtesy.scopeName || "").trim().toLowerCase();

    if (scopeId && targetCId && scopeId === targetCId) return true;
    if (scopeName && targetCName && (scopeName === targetCName || targetCName.includes(scopeName))) return true;
    return false;
  }

  return false;
}

export interface CommercialResolutionContext {
  court?: Partial<CourtItem> | null;
  arena?: {
    id?: string;
    name?: string;
    customBookingCommissionRate?: number;
    customCommissionRate?: number;
    tournamentCourtRentalCommissionRate?: number;
    tournamentCourtRentalCommissionMode?: "ADD" | "ABSORB";
    customTicketCommissionRate?: number;
    isCourtesyActive?: boolean;
  } | null;
  date?: string; // YYYY-MM-DD
  courtesyList?: CommercialCourtesy[];
  sysSettings?: Partial<SystemSettings>;
}

/**
 * MOTOR CENTRAL DE RESOLUÇÃO HIERÁRQUICA:
 * 1. EXCEÇÃO / CORTESIA (Se ativa e dentro do período de validade)
 * 2. QUADRA (Se houver override explícito)
 * 3. ARENA (Se houver override explícito)
 * 4. PLATAFORMA (Padrão global do sistema)
 */
export function resolveCommercialRule(
  service: CommercialServiceType,
  context?: CommercialResolutionContext
): ResolvedCommercialRule {
  const sys = context?.sysSettings ? { ...getSystemSettings(), ...context.sysSettings } : getSystemSettings();
  const targetDate = context?.date || new Date().toISOString().substring(0, 10);
  const court = context?.court;
  const arena = context?.arena;

  const courtId = court?.id;
  const courtName = court?.courtName;
  const arenaId = arena?.id || court?.arenaId;
  const arenaName = arena?.name || court?.arenaName;

  // -------------------------------------------------------------
  // NÍVEL 1: EXCEÇÕES & CORTESIAS COMERCIAIS
  // -------------------------------------------------------------
  const courtesies = context?.courtesyList || loadCommercialCourtesies();
  const activeCourtesies = courtesies.filter((c) =>
    isCourtesyValidForContext(c, service, targetDate, courtId, courtName, arenaId, arenaName)
  );

  if (activeCourtesies.length > 0) {
    // Prioridade dentro das cortesias: QUADRA > ARENA > PLATAFORMA
    activeCourtesies.sort((a, b) => {
      const weight = { COURT: 3, ARENA: 2, PLATFORM: 1 };
      return (weight[b.scope] || 0) - (weight[a.scope] || 0);
    });

    const chosenCourtesy = activeCourtesies[0];
    let effectiveRate = 0;
    if (chosenCourtesy.exemptionType === "CUSTOM_RATE") {
      effectiveRate = Number(chosenCourtesy.customRate || 0);
    }

    return {
      service,
      effectiveRate,
      source: "COURTESY",
      sourceId: chosenCourtesy.id,
      sourceName: `Cortesia: ${chosenCourtesy.title} (${chosenCourtesy.scope})`,
      isExempt: chosenCourtesy.exemptionType !== "CUSTOM_RATE" || effectiveRate === 0,
      exemptionType: chosenCourtesy.exemptionType,
      courtesyApplied: chosenCourtesy,
      reason: chosenCourtesy.reason,
      ruleVersion: COMMERCIAL_RULE_VERSION,
    };
  }

  // -------------------------------------------------------------
  // NÍVEL 2: QUADRA ESPECÍFICA (OVERRIDE DE QUADRA)
  // -------------------------------------------------------------
  if (service === "BOOKING" && court && court.customCommissionRate !== undefined && court.customCommissionRate !== null) {
    const rate = Number(court.customCommissionRate);
    if (!isNaN(rate) && rate >= 0) {
      return {
        service,
        effectiveRate: rate,
        source: "COURT",
        sourceId: court.id,
        sourceName: `Quadra: ${court.courtName || "Quadra Customizada"}`,
        isExempt: rate === 0,
        ruleVersion: COMMERCIAL_RULE_VERSION,
      };
    }
  }

  if (service === "DAY_USE" && court && (court as any).customDayUseCommissionRate !== undefined && (court as any).customDayUseCommissionRate !== null) {
    const rate = Number((court as any).customDayUseCommissionRate);
    if (!isNaN(rate) && rate >= 0) {
      return {
        service,
        effectiveRate: rate,
        source: "COURT",
        sourceId: court.id,
        sourceName: `Quadra: ${court.courtName || "Quadra Customizada"}`,
        isExempt: rate === 0,
        ruleVersion: COMMERCIAL_RULE_VERSION,
      };
    }
  }

  if (service === "TOURNAMENT_RENTAL" && court) {
    const rentalRate = (court as any).tournamentCourtRentalCommissionRate;
    const rentalMode = (court as any).tournamentCourtRentalCommissionMode;
    if (rentalRate !== undefined && rentalRate !== null && typeof rentalRate === "number") {
      return {
        service,
        effectiveRate: rentalRate,
        rentalMode: rentalMode === "ABSORB" ? "ABSORB" : "ADD",
        source: "COURT",
        sourceId: court.id,
        sourceName: `Quadra: ${court.courtName || "Quadra Customizada"}`,
        isExempt: rentalRate === 0,
        ruleVersion: COMMERCIAL_RULE_VERSION,
      };
    }
  }

  // -------------------------------------------------------------
  // NÍVEL 3: ARENA (OVERRIDE DE ARENA)
  // -------------------------------------------------------------
  if (arena) {
    if (service === "BOOKING") {
      const arenaBookingRate = arena.customBookingCommissionRate ?? arena.customCommissionRate;
      if (arenaBookingRate !== undefined && arenaBookingRate !== null && typeof arenaBookingRate === "number") {
        return {
          service,
          effectiveRate: arenaBookingRate,
          source: "ARENA",
          sourceId: arena.id || arenaId,
          sourceName: `Arena: ${arena.name || arenaName || "Arena"}`,
          isExempt: arenaBookingRate === 0,
          ruleVersion: COMMERCIAL_RULE_VERSION,
        };
      }
    }

    if (service === "TOURNAMENT_RENTAL") {
      const arenaRentalRate = arena.tournamentCourtRentalCommissionRate;
      const arenaRentalMode = arena.tournamentCourtRentalCommissionMode;
      if (arenaRentalRate !== undefined && arenaRentalRate !== null && typeof arenaRentalRate === "number") {
        return {
          service,
          effectiveRate: arenaRentalRate,
          rentalMode: arenaRentalMode === "ABSORB" ? "ABSORB" : "ADD",
          source: "ARENA",
          sourceId: arena.id || arenaId,
          sourceName: `Arena: ${arena.name || arenaName || "Arena"}`,
          isExempt: arenaRentalRate === 0,
          ruleVersion: COMMERCIAL_RULE_VERSION,
        };
      }
    }

    if (service === "DAY_USE") {
      const arenaDayUseRate = (arena as any).customDayUseCommissionRate ?? arena.customTicketCommissionRate;
      if (arenaDayUseRate !== undefined && arenaDayUseRate !== null) {
        return {
          service: "DAY_USE",
          effectiveRate: Number(arenaDayUseRate),
          source: "ARENA",
          sourceId: arena.id || arenaId,
          sourceName: `Arena: ${arena.name || arenaName || "Arena"}`,
          isExempt: Number(arenaDayUseRate) === 0,
          ruleVersion: COMMERCIAL_RULE_VERSION,
        };
      }
    }

    if (service === "TICKET" && arena.customTicketCommissionRate !== undefined && arena.customTicketCommissionRate !== null) {
      return {
        service,
        effectiveRate: Number(arena.customTicketCommissionRate),
        source: "ARENA",
        sourceId: arena.id || arenaId,
        sourceName: `Arena: ${arena.name || arenaName || "Arena"}`,
        isExempt: Number(arena.customTicketCommissionRate) === 0,
        ruleVersion: COMMERCIAL_RULE_VERSION,
      };
    }
  }

  // -------------------------------------------------------------
  // NÍVEL 4: PLATAFORMA (PADRÃO GLOBAL)
  // -------------------------------------------------------------
  if (service === "BOOKING") {
    const globalRate = Number(sys.courtCommissionRate ?? sys.platformCommissionRate ?? 4.0);
    return {
      service,
      effectiveRate: globalRate,
      source: "PLATFORM",
      sourceName: "Plataforma (Padrão Global)",
      isExempt: globalRate === 0,
      ruleVersion: COMMERCIAL_RULE_VERSION,
    };
  }

  if (service === "TOURNAMENT_RENTAL") {
    const globalRentalRate = Number(sys.tournamentCourtRentalCommissionRate ?? 10);
    const globalRentalMode = sys.tournamentCourtRentalCommissionMode === "ABSORB" ? "ABSORB" : "ADD";
    return {
      service,
      effectiveRate: globalRentalRate,
      rentalMode: globalRentalMode,
      source: "PLATFORM",
      sourceName: "Plataforma (Padrão Global)",
      isExempt: globalRentalRate === 0,
      ruleVersion: COMMERCIAL_RULE_VERSION,
    };
  }

  if (service === "TOURNAMENT_REGISTRATION") {
    const isEnabled = sys.tournamentCommissionEnabled !== false;
    const globalTournamentRate = isEnabled ? Number(sys.tournamentCommissionRate ?? sys.platformCommissionRate ?? 4.0) : 0;
    return {
      service,
      effectiveRate: globalTournamentRate,
      source: "PLATFORM",
      sourceName: "Plataforma (Padrão Global)",
      isExempt: globalTournamentRate === 0 || !isEnabled,
      reason: !isEnabled ? "Taxa da plataforma para inscrições desativada pelo Super Admin" : undefined,
      ruleVersion: COMMERCIAL_RULE_VERSION,
    };
  }

  if (service === "DAY_USE") {
    const globalDayUseRate = Number(sys.dayUseCommissionRate ?? 5.0);
    return {
      service: "DAY_USE",
      effectiveRate: globalDayUseRate,
      source: "PLATFORM",
      sourceName: "Plataforma (Padrão Global)",
      isExempt: globalDayUseRate === 0,
      ruleVersion: COMMERCIAL_RULE_VERSION,
    };
  }

  // TICKET
  const globalTicketRate = Number(sys.ticketCommissionRate ?? 3.0);
  return {
    service: "TICKET",
    effectiveRate: globalTicketRate,
    source: "PLATFORM",
    sourceName: "Plataforma (Padrão Global)",
    isExempt: globalTicketRate === 0,
    ruleVersion: COMMERCIAL_RULE_VERSION,
  };
}

/**
 * Cria o snapshot imutável para uma operação financeira.
 */
export function createCommercialOperationSnapshot(
  rule: ResolvedCommercialRule,
  grossAmount: number,
  additionalFinancials?: {
    gatewayFeeAmount?: number;
    entryFeeAmount?: number;
    organizerTotalToPay?: number;
    arenaNetAmount?: number;
  }
): CommercialOperationSnapshot {
  const gross = Math.max(0, Number(grossAmount) || 0);
  const feeRate = rule.effectiveRate;
  const platformFeeAmount = Number(((gross * feeRate) / 100).toFixed(2));

  return {
    ruleVersion: rule.ruleVersion,
    service: rule.service,
    effectiveRate: feeRate,
    source: rule.source,
    sourceId: rule.sourceId,
    sourceName: rule.sourceName,
    isExempt: rule.isExempt,
    courtesyId: rule.courtesyApplied?.id,
    courtesyTitle: rule.courtesyApplied?.title,
    appliedAt: new Date().toISOString(),
    financialBreakdown: {
      grossAmount: gross,
      platformFeeRate: feeRate,
      platformFeeAmount,
      arenaNetAmount: additionalFinancials?.arenaNetAmount,
      organizerTotalToPay: additionalFinancials?.organizerTotalToPay,
      entryFeeAmount: additionalFinancials?.entryFeeAmount,
      gatewayFeeAmount: additionalFinancials?.gatewayFeeAmount,
    },
  };
}
